import { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapImage from '@tiptap/extension-image';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2,
  Heading1, Heading2, Heading3, Image as ImageIcon, GitBranch, Loader2, Search, X as XIcon,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

/**
 * ProcessRichTextEditor — Tiptap-Rich-Text fuer Prozess-Beschreibungen (#1472, BR-35).
 *
 * Erweitert das Cases-Pattern (components/RichTextEditor.jsx) um Ueberschriften,
 * Inline-Bilder (Upload+URL ueber den bestehenden prozess_medien-Endpoint) und
 * Prozess-Verlinkung. Speichert HTML (editor.getHTML()), wird via ProcessHtmlView
 * + DOMPurify gerendert. Die Cases-Instanz bleibt unangetastet (AK6).
 *
 * Props:
 *  - value (string, HTML): controlled HTML-Wert
 *  - onChange (fn): HTML bei jeder Aenderung
 *  - prozessId (number): noetig fuer Bild-Upload (POST /api/prozesse/:id/medien)
 *  - placeholder (string)
 *  - minHeight (px, default 240) — Flaeche waechst dynamisch mit dem Inhalt
 */
export default function ProcessRichTextEditor({
  value,
  onChange,
  prozessId,
  placeholder = '',
  minHeight = 240,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
      }),
      TiptapLink.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
      TiptapUnderline,
      TiptapImage.configure({ inline: false, allowBase64: false }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none overflow-y-auto px-4 py-3 text-sm focus:outline-none text-[var(--text-primary,#1A1A2E)] prozess-richtext',
        style: `min-height: ${minHeight}px; max-height: 70vh;`,
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.isEmpty ? '' : editor.getHTML());
    },
  });

  // Sync external value changes (parent reset) ohne Cursor-Reset bei Tipp-Events
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && !editor.isFocused) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  // ── Bild-Upload (Upload+URL, keine CIDs) ──
  const handleImagePick = () => fileInputRef.current?.click();

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    if (!prozessId) { window.alert('Bild-Upload erst nach dem Speichern des Prozesses moeglich.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/prozesse/${prozessId}/medien`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error('upload failed');
      const data = await res.json();
      const dateipfad = data.medium?.dateipfad;
      if (!dateipfad) throw new Error('no path');
      const src = `${API}/api/prozesse/medien/stream/${dateipfad}`;
      editor.chain().focus().setImage({ src, alt: data.medium?.dateiname || '' }).run();
    } catch {
      window.alert('Bild konnte nicht hochgeladen werden.');
    } finally {
      setUploading(false);
    }
  };

  // ── Prozess-Verlinkung ──
  const searchProzesse = useCallback(async (q) => {
    setLinkLoading(true);
    try {
      const url = `${API}/api/prozesse?limit=10${q ? `&suche=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setLinkResults((data.prozesse || data.items || data.rows || []).filter(p => p.id !== prozessId));
    } catch {
      setLinkResults([]);
    } finally {
      setLinkLoading(false);
    }
  }, [prozessId]);

  useEffect(() => {
    if (!linkPickerOpen) return;
    const tmr = setTimeout(() => searchProzesse(linkQuery), 250);
    return () => clearTimeout(tmr);
  }, [linkQuery, linkPickerOpen, searchProzesse]);

  const openLinkPicker = () => { setLinkQuery(''); setLinkResults([]); setLinkPickerOpen(true); };

  const insertProzessLink = (p) => {
    if (!editor) return;
    const href = `/prozesse/${p.id}`;
    const label = p.titel || `Prozess ${p.id}`;
    editor.chain().focus()
      .insertContent({
        type: 'text',
        text: label,
        marks: [{ type: 'link', attrs: { href } }],
      })
      .insertContent(' ')
      .run();
    setLinkPickerOpen(false);
  };

  // ── externer Link (URL) ──
  const toggleLink = () => {
    if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
    const url = window.prompt('URL');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  if (!editor) return null;

  const ToolBtn = ({ active, onClick, children, title, disabled }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-colors disabled:opacity-40 ${
        active
          ? 'bg-[#338edc]/10 text-[#338edc]'
          : 'text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-hover,#FAFAFE)]'
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <span className="w-px h-4 bg-[var(--border-card,#E5E7EB)] mx-1" />;

  return (
    <div
      className="border rounded-card bg-[var(--bg-card,#fff)] border-[var(--border-default,#E5E7EB)] focus-within:border-[#338edc] focus-within:ring-2 focus-within:ring-[#338edc]/10 transition-colors relative"
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1 border-b border-[var(--border-card,#E5E7EB)]">
        <ToolBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Ueberschrift 1">
          <Heading1 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Ueberschrift 2">
          <Heading2 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Ueberschrift 3">
          <Heading3 className="w-4 h-4" />
        </ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Fett">
          <Bold className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kursiv">
          <Italic className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Unterstrichen">
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Aufzaehlung">
          <List className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Nummerierte Liste">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive('link')} onClick={toggleLink} title="Link">
          <Link2 className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={handleImagePick} title="Bild einfuegen" disabled={uploading}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
        </ToolBtn>
        <ToolBtn active={linkPickerOpen} onClick={openLinkPicker} title="Prozess verlinken">
          <GitBranch className="w-3.5 h-3.5" />
        </ToolBtn>
      </div>

      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={handleImageFile} />

      <EditorContent editor={editor} />

      {linkPickerOpen && (
        <div className="absolute z-20 top-12 right-2 w-80 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-card,#E5E7EB)]">
            <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">Prozess verlinken</span>
            <button type="button" onClick={() => setLinkPickerOpen(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="p-2">
            <div className="flex items-center gap-2 px-2 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg">
              <Search className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" />
              <input
                autoFocus
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                placeholder="Prozess suchen..."
                className="flex-1 text-sm bg-transparent focus:outline-none text-[var(--text-primary,#1A1A2E)]"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto">
              {linkLoading ? (
                <div className="flex items-center justify-center py-4 text-[var(--text-tertiary,#6B7280)]"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : linkResults.length === 0 ? (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] px-2 py-3">Keine Prozesse gefunden.</p>
              ) : (
                linkResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => insertProzessLink(p)}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[var(--bg-hover,#FAFAFE)] text-sm text-[var(--text-primary,#1A1A2E)] truncate"
                  >
                    {p.titel}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
